import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CHAT_ANIMATIONS } from '@/utils/chat-animations';
import { FileText, Plus, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GlassPill from '@/components/ui/GlassPill';
import { useAgentChatPageCreationWithTracking } from '@/hooks/ai-agents/functions/core/useAgentChatPageCreationWithTracking';
import { useRecommendationCardState, RECOMMENDATION_CONFIGS } from '@/hooks/ai-agents/functions/core/createRecommendationTrackingWrapper';
import { useRecommendationTracking } from '@/hooks/ai-agents/functions/core/useRecommendationTracking';
import { useViewRecommendationNavigation } from '@/hooks/ai-agents/functions/core/useViewRecommendationNavigation';
import { useThreadContext } from '@/hooks/ai-agents/functions/core/useThreadContext';
import { useNavigate } from 'react-router-dom';
import { 
  getProjectInfoById,
  getProjectNavigationUrl,
  getGoalNavigationUrl,
  useNavigationWithModalClose
} from '@/hooks/ai-agents/functions/core/useResponseParsing';
import { cn } from '@/lib/utils';
import { ExtractedPage } from '@/hooks/ai-agents/functions/core/useRecommendationDetection';
import { MessageDisplay } from '@/hooks/ai-agents/types/chat-hook-types';
import { 
  getCardContainerClasses, 
  getCardIconClasses, 
  getCardButtonClasses 
} from './shared/RecommendationCardStyles';

interface ChatRecommendedPageCardProps {
  page: ExtractedPage;
  threadId: string;
  message: MessageDisplay; // Changed from messageId: string to message: MessageDisplay
  type?: 'page';
  className?: string;
}

type SaveState = 'idle' | 'saving' | 'success' | 'saved' | 'error';

/**
 * Chat Recommended Page Card - Individual page item with save button
 * Shows page recommendations with option to save to project workbook
 * Now with standardized recommendation tracking
 */
export const ChatRecommendedPageCard: React.FC<ChatRecommendedPageCardProps> = ({
  page,
  threadId,
  message, // Changed from messageId to message
  type = 'page',
  className
}) => {
  const { navigateAndCloseModal } = useNavigationWithModalClose();
  
  // Get thread context to find the workbook ID
  const { data: threadContext } = useThreadContext(threadId);
  
  // Use standardized tracking state management with full message object
  const { localState, setLocalState } = useRecommendationCardState(message, 'page');
  
  // Page creation with tracking using full message object
  const { mutateWithTracking, isGenerating } = useAgentChatPageCreationWithTracking(message);
  
  // Get saved recommendation data for view functionality using full message object
  const { isRecommendationSaved } = useRecommendationTracking(message.id, message.databaseId);
  
  // View navigation functionality
  const { viewPage } = useViewRecommendationNavigation();
  
  // Handle viewing the saved page
  const handleViewPage = async () => {
    const status = await isRecommendationSaved('page');
    if (status.isSaved && status.savedItemId && status.metadata) {
      const savedRecommendation = {
        id: status.savedItemId,
        type: 'page' as const,
        savedAt: status.savedAt!,
        metadata: status.metadata
      };
      viewPage(savedRecommendation);
    } else {
      console.error('[ChatRecommendedPageCard] No saved page data found');
    }
  };
  
  // Get config for this recommendation type
  const config = RECOMMENDATION_CONFIGS.page;
  
  // Pages now always render and go to private text library (no project dependencies)

  // Handle saving this specific page
  const handleSavePage = async () => {
    console.log('[ChatRecommendedPageCard] 🚀 STARTING handleSavePage - Private Page');
    
    setLocalState('saving');
    
    try {
      const result = await mutateWithTracking({
        page,
        messageId: message.id // Use message.id for consistency
        // No workbookId or goalThreadId - private page
      });
      
      setLocalState('saved');
      
    } catch (error) {
      console.error('[ChatRecommendedPageCard] Failed to save page:', error);
      setLocalState('error');
      
      // Reset to idle after 3 seconds
      setTimeout(() => {
        setLocalState('idle');
      }, 3000);
    }
  };

  // Get button content based on state
  const getButtonContent = () => {
    switch (localState) {
      case 'saving':
        return <Loader2 size={12} className="animate-spin" />;
      case 'success':
        return <Check size={12} />;
      case 'saved':
        return <FileText size={12} />;
      case 'error':
        return <AlertCircle size={12} />;
      default:
        return <Plus size={12} />;
    }
  };

  // Get button text based on state
  const getButtonText = () => {
    switch (localState) {
      case 'saving':
        return config.savingText;
      case 'success':
        return config.successText;
      case 'saved':
        return config.savedText;
      case 'error':
        return config.errorText;
      default:
        return config.saveText;
    }
  };

  // Get button click handler based on state
  const getButtonHandler = () => {
    switch (localState) {
      case 'saved':
        return handleViewPage;
      default:
        return handleSavePage;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        variants={CHAT_ANIMATIONS.cardEntry}
        initial="initial"
        animate="animate"
        exit="exit"
        className={cn("mt-3 w-full", className)}
      >
        <div className={cn(getCardContainerClasses(type))}>
          <div className="flex items-start gap-2.5">
            {/* Save Button - Left Side */}
            <div className="flex-shrink-0 pt-0.5">
              <Button
                size="sm"
                variant="outline"
                onClick={getButtonHandler()}
                disabled={localState === 'saving' || localState === 'success'}
                className={cn(
                  "h-7 px-3 text-xs border-purple-300 dark:border-purple-600",
                  "hover:bg-purple-50 dark:hover:bg-purple-900/20",
                  "transition-all duration-200",
                  // State-specific styling
                  localState === 'success' && 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
                  localState === 'saved' && 'border-gray-300 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200',
                  localState === 'error' && 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
                )}
              >
                <span className="flex items-center gap-1.5">
                  {getButtonContent()}
                  <span>{getButtonText()}</span>
                </span>
              </Button>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-gray-700 dark:text-gray-200 block font-medium">
                  {page.title}
                </span>
                <GlassPill type={page.type} variant="purple" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                {page.description}
              </p>
              
              {/* Private Page - No project navigation needed */}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}; 